/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   MaintenanceDashboard.tsx
 *     Description     :   Main maintenance dashboard page. Conformed to Spec 197.
 *     Author          :   Peter Dobler
 *     Created         :   2026-01-19
 *     Modified        :   2026-06-15 | Conformed to the Spec 197 design blueprint —
 *                         migrated off MUI (Container/Paper/Grid/Tabs/Typography)
 *                         onto the canonical Tailwind+token UI kit. Presentation-only;
 *                         no data/logic/state/route/behavior changes.
 *     Constitution    :   v1.19.0
 *     Feature         :   028-lakehouse-maintenance
 * =========================================================================================
 */

import React, { useState } from 'react';
import { clsx } from 'clsx';
import { Plus } from 'lucide-react';
import {
  PageLayout,
  Card,
  Button,
  StatCard,
  ErrorState,
  AppTooltip,
  PageHelpPanel,
  NotCollected,
  Spinner,
} from '../components/ui';
import { useDashboardSummary } from '../hooks/useMaintenanceData';
import HealthScoreCard from '../components/maintenance/HealthScoreCard';
import LakehouseHealthTable from '../components/maintenance/LakehouseHealthTable';
import PolicyList from '../components/maintenance/PolicyList';
import PolicyForm from '../components/maintenance/PolicyForm';
import { deployOpsHelp } from '../data/help/deployOpsHelp';
import { formatEastern } from '@/utils/dateFormatter';

// Spec 298 FR-006 (evidence/zero-fallbacks.md rows 55-62; owner decision, Session 2026-11-04): GET
// /api/v1/maintenance/dashboard sends {summary: {totalOperations, ...}, recentOperations, filters}
// (backend modules/maintenance/routes/maintenanceRoutes.ts), with no lakehouse health and no pending or running
// counts, so each card printed 0. A card shows its figures only when the body carries them; otherwise it says
// "not collected". Making maintenance health work belongs to Spec 180.
const HEALTH_REASON =
  'The maintenance API does not report lakehouse health: GET /api/v1/maintenance/dashboard sends operation history only.';
const OPERATIONS_REASON =
  'The maintenance API does not report pending or running operations: GET /api/v1/maintenance/dashboard sends operation history only.';

const HEALTH_CARDS = [
  { status: 'healthy', title: 'Healthy', tooltip: 'Lakehouses with all health checks passing' },
  { status: 'degraded', title: 'Degraded', tooltip: undefined },
  { status: 'unhealthy', title: 'Unhealthy', tooltip: undefined },
] as const;

export const MaintenanceDashboard: React.FC = () => {
  const { data: summary, error, refetch } = useDashboardSummary();
  const [activeTab, setActiveTab] = useState(0);
  const [createPolicyOpen, setCreatePolicyOpen] = useState(false);

  const tabs = ['Health & Operations', 'Policies'];
  // No body yet: pending, fetching or paused (TanStack Query's isLoading is false for a paused query).
  const pendingCard = <Spinner size="sm" label="Loading" />;
  const health = summary?.healthSummary;
  const stats = summary?.maintenanceStats;

  return (
    <PageLayout
      title="Lakehouse Maintenance Dashboard"
      description="Monitor lakehouse health and maintenance operations"
      actions={<PageHelpPanel content={deployOpsHelp} buttonSize="sm" />}
    >
      {error ? (
        <ErrorState
          title="Failed to load dashboard data"
          message="Failed to load dashboard data. Please try again later."
          onRetry={() => refetch()}
        />
      ) : (
        <>
          {/* Health Summary Cards */}
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {HEALTH_CARDS.map(({ status, title, tooltip }) => {
              const card = health ? (
                <HealthScoreCard title={title} count={health[status]} total={health.total} status={status} />
              ) : (
                <StatCard
                  label={title}
                  value={summary ? <NotCollected reason={HEALTH_REASON} /> : pendingCard}
                />
              );
              return tooltip ? (
                <AppTooltip key={status} title={tooltip}>
                  {card}
                </AppTooltip>
              ) : (
                <React.Fragment key={status}>{card}</React.Fragment>
              );
            })}
            <StatCard
              label="Operations"
              value={
                stats
                  ? stats.pendingOperations + stats.runningOperations
                  : summary
                    ? <NotCollected reason={OPERATIONS_REASON} />
                    : pendingCard
              }
              valueClassName="text-[var(--color-accent)]"
              subtext={
                stats ? (
                  <>
                    Pending: {stats.pendingOperations}
                    {' · '}
                    Running: {stats.runningOperations}
                  </>
                ) : undefined
              }
            />
          </div>

          {/* Tabs */}
          <div className="mb-4 flex gap-1 border-b border-[var(--color-border)]">
            {tabs.map((label, i) => (
              <button
                key={label}
                onClick={() => setActiveTab(i)}
                className={clsx(
                  '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors',
                  activeTab === i
                    ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                    : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Tab 0: Health & Operations */}
          {activeTab === 0 && (
            <>
              {/* Lakehouse Health Table */}
              <div className="mb-6">
                <h2 className="mb-2 text-lg font-semibold text-[var(--color-text-primary)]">
                  Lakehouse Health
                </h2>
                <LakehouseHealthTable />
              </div>

              {/* Recent Activity */}
              {summary?.recentActivity && summary.recentActivity.length > 0 && (
                <div>
                  <h2 className="mb-2 text-lg font-semibold text-[var(--color-text-primary)]">
                    Recent Activity
                  </h2>
                  <Card>
                    {summary.recentActivity.map((activity: any) => (
                      <div
                        key={activity.id}
                        className="mb-3 border-b border-[var(--color-border)] pb-3 last:mb-0 last:border-0 last:pb-0"
                      >
                        <p className="text-sm text-[var(--color-text-primary)]">
                          {activity.operation_type} on {activity.lakehouse_name || 'Lakehouse'}
                        </p>
                        <p className="text-xs text-[var(--color-text-secondary)]">
                          {formatEastern(activity.completed_at || activity.created_at)}
                        </p>
                      </div>
                    ))}
                  </Card>
                </div>
              )}
            </>
          )}

          {/* Tab 1: Policies */}
          {activeTab === 1 && (
            <div>
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
                  Maintenance Policies
                </h2>
                <AppTooltip title="Create a new automated maintenance policy for lakehouses">
                  <Button
                    variant="primary"
                    leftIcon={<Plus className="h-4 w-4" />}
                    onClick={() => setCreatePolicyOpen(true)}
                  >
                    Create Policy
                  </Button>
                </AppTooltip>
              </div>
              <PolicyList />
            </div>
          )}

          {/* Policy Creation Dialog */}
          <PolicyForm open={createPolicyOpen} onClose={() => setCreatePolicyOpen(false)} mode="create" />
        </>
      )}
    </PageLayout>
  );
};

export default MaintenanceDashboard;
